import { inject, injectable, named } from 'inversify';
import { v4 as uuid } from 'uuid';

import StorageService from '../../StorageService';
import type { PlaylistItem } from '../../../../types/playlist';
import EntitlementService from '../../EntitlementService';
import { createURL } from '../../../utils/urlFormatting';
import AccountService from '../AccountService';
import { logError } from '../../../logger';

import StreamIQApiService from './StreamIQApiService';
import StreamIQAccountService from './StreamIQAccountService';

type Session = {
  id: string;
  profileId: string;
  streamingProtocol: string;
  deviceFingerprint: string;
  playInfo: {
    url: string;
    positionInMs: number;
    timeline?: { type: string; positionInMs: number }[];
    drm?: {
      widevine?: { licenseAcquisitionURL: string };
    };
  };
};

@injectable()
export default class StreamIQEntitlementService extends EntitlementService {
  readonly playbackRetryCount = 0;

  private deviceId: string | undefined;
  private currentSession: Session | undefined;

  constructor(
    @inject(StreamIQApiService) private readonly apiService: StreamIQApiService,
    @inject(AccountService) @named('STREAMIQ') private readonly accountService: StreamIQAccountService,
    @inject(StorageService) private readonly storageService: StorageService,
  ) {
    super();
  }

  async getMediaToken(): Promise<string> {
    return '';
  }

  async getDeviceId() {
    if (this.deviceId) return this.deviceId;

    const existingDeviceId = await this.storageService.getItem('device-id', false);
    if (typeof existingDeviceId === 'string') {
      this.deviceId = existingDeviceId;
    }

    if (!this.deviceId) {
      this.deviceId = uuid();
      await this.storageService.setItem('device-id', this.deviceId);
    }

    return this.deviceId!;
  }

  async stopAllDeviceSessions() {
    try {
      const profileId = this.accountService.profileId!;
      const deviceFingerprint = await this.getDeviceId();
      const response = await this.apiService.get<{ resources: Session[] }>(`/session-service/sessions?profileId=${profileId}`, { withAuthentication: true });

      // ensure that all sessions are stopped for this device fingerprint
      await Promise.allSettled(
        response.resources.map((session) => {
          if (session.deviceFingerprint === deviceFingerprint) {
            return this.apiService.remove(`/session-service/sessions/${session.id}`, { withAuthentication: true });
          }
        }),
      );
    } catch (error: unknown) {
      logError('StreamIQEntitlementService', 'Error while stopping play sessions', { error });
    }
  }

  async startSession(
    item: PlaylistItem,
    _signedItem?: PlaylistItem,
    startTime = 0,
    streamingProtocol: 'dash' | 'hls' = 'dash',
  ): Promise<{ playableItem: PlaylistItem; startOffset: number }> {
    const streamIqResource = item.mediaid.split('_')[0];
    const streamIqId = item.mediaid.split('_').slice(1).join('_');
    const deviceFingerprint = await this.getDeviceId();

    await this.stopAllDeviceSessions();

    try {
      this.currentSession = await this.apiService.post<Session>(
        '/session-service/sessions',
        {
          profileId: this.accountService.profileId,
          streamingProtocol,
          deviceFingerprint,
          reference: {
            id: streamIqResource === 'promotional' ? streamIqId.split('_')[1] : streamIqId,
            type: streamIqResource,
            serviceName:
              streamIqResource === 'channel' || streamIqResource === 'event' || streamIqResource === 'episodeEvent' || streamIqResource === 'movieEvent'
                ? 'epg-service'
                : 'vod-service',
          },
        },
        { withAuthentication: true },
      );

      const widevine = this.currentSession?.playInfo.drm?.widevine;
      const drm = widevine ? { widevine: { url: widevine.licenseAcquisitionURL } } : undefined;

      const timeline = this.currentSession?.playInfo.timeline;
      const mainContentOffset = (timeline?.find(({ type }) => type === 'main')?.positionInMs || 0) / 1000;
      const contentDuration = (timeline?.find(({ type }) => type === 'end')?.positionInMs || 0) / 1000;

      // small compensation for when `item.duration` differs from the actual stream duration (i.e.: live programs that include a contentOffset)
      const startOffset = startTime > 0 ? startTime * ((contentDuration || item.duration) / item.duration) : mainContentOffset;

      return {
        startOffset,
        playableItem: {
          ...item,
          sources: [
            {
              file: this.currentSession.playInfo.url,
              type: streamingProtocol === 'dash' ? 'application/dash+xml' : 'application/vnd.apple.mpegurl',
              ...(drm ? { drm } : {}),
            },
          ],
        },
      };
    } catch (error: unknown) {
      if (error instanceof Error) {
        if (error.message.includes('Max concurrent sessions')) throw new Error('too many sessions');
        throw error; // re-throw
      }

      throw new Error('Failed to start play session');
    }
  }

  async stopSession(positionInMs: number): Promise<void> {
    if (!this.currentSession) return;

    try {
      await this.apiService.remove(createURL(`/session-service/sessions/${this.currentSession.id}`, { positionInMs }), { withAuthentication: true });
    } catch (error: unknown) {
      logError('StreamIQEntitlementService', 'Error while stopping the current play session', { error });
    } finally {
      this.currentSession = undefined;
    }
  }
}
